import { InfiniteData } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Event } from '@/Domain/api/Conversation/dto/Event.dto';
import { Paginated } from '@/Domain/api/Shared/dto/Paginated.dto';
import { LinkClickItem } from '@/Domain/components/Inbox/Message/LinkClickItem';
import { MessageItem } from '@/Domain/components/Inbox/Message/MessageItem';
import { NoteItem } from '@/Domain/components/Inbox/Message/NoteItem';
import { TimestampItem } from '@/Domain/components/Inbox/Message/TimestampItem';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { EventTypeEnum } from '@/Domain/enum/EventType.enum';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';
import { useGetConversation } from '@/Domain/hooks/Conversation/useGetConversation.hook';

type Props = {
  id: string;
  events: InfiniteData<Paginated<Event[]>, unknown> | undefined;
  loading: boolean;
  hasNextPage: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
};

export const MessageList = ({ events, onLoadMore, isLoading, loading, hasNextPage, id }: Props) => {
  const messagesCountRef = useRef<number>(0);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const { conversation } = useGetConversation(id);

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore,
  });

  const scrollToBottom = (): void => {
    if (messagesEndRef.current !== null) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useEffect(() => {
    const itemsCount = (events?.pages || []).reduce((count, page) => count + page.items.length, 0);

    if (messagesCountRef.current !== itemsCount) {
      messagesCountRef.current = itemsCount;
      scrollToBottom();
    }
  }, [events]);

  return (
    <ul className="list-none m-0 flex-grow overflow-y-auto flex flex-col gap-8 hide-scrollbar">
      {events?.pages
        .map(({ items }) => items)
        .flat()
        .reverse()
        .map((event, index, allEvents) => {
          if (event.event_type === EventTypeEnum.MESSAGE) {
            return (
              <MessageItem
                key={event.data.id}
                message={event.data}
                conversation={conversation}
                previousMessage={
                  index > 0 ? ConversationHelper.getPreviousMessage(allEvents, index) : null
                }
              />
            );
          }

          if (event.event_type === EventTypeEnum.NOTE) {
            return <NoteItem key={event.created_at} note={event.data} />;
          }

          if (event.event_type === EventTypeEnum.LOG) {
            return (
              <li key={event.created_at} className="text-foreground-subtle text-sm text-center">
                <span dangerouslySetInnerHTML={{ __html: event.data.description }}></span>
              </li>
            );
          }

          if (event.event_type === EventTypeEnum.TIMESTAMP) {
            return <TimestampItem key={event.created_at} timestamp={event.created_at} />;
          }

          if (event.event_type === EventTypeEnum.LINK_CLICKED) {
            return (
              <LinkClickItem
                key={`${event.created_at}-${event.data.tracked_url_id}`}
                linkClick={event.data}
                timestamp={event.created_at}
              />
            );
          }

          return null;
        })}

      {(loading || hasNextPage || isLoading) && (
        <li ref={sentryRef} className="mx-6">
          <div className="flex justify-center items-center py-6">
            <Spinner />
          </div>
        </li>
      )}
      <div ref={messagesEndRef} />
    </ul>
  );
};
